/**
 * clientReporter (Jose 2026-10-13) — give us EYES on what clients hit, and clients a VOICE.
 *  • installErrorCapture()  — global window.error + unhandledrejection → logs to `clientErrors` (so we
 *    see breakages live instead of hearing them third-hand; the gap that cost us on launch day).
 *  • sendClientMessage()    — the portal "Need help?" button → writes to `clientMessages` with the last
 *    few errors from this session attached, so a report comes with context.
 * Write-only logs; staff read them (rules). The logger NEVER throws (can't make a bad day worse).
 */
import { db, auth } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const sessionErrors: Array<{ message: string; at: string }> = [];
let errCount = 0;
const MAX_ERRORS = 15; // don't spam Firestore if a page error-loops

function ctx() {
  const u = auth.currentUser;
  return {
    uid: u?.uid || null,
    email: u?.email || null,
    page: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
    ua: typeof window !== 'undefined' ? window.navigator.userAgent : '',
  };
}

export async function logClientError(message: string, stack?: string, kind = 'error') {
  try {
    if (errCount >= MAX_ERRORS) return;
    errCount++;
    const entry = { message: String(message || '').slice(0, 500), at: new Date().toISOString() };
    sessionErrors.push(entry);
    if (sessionErrors.length > 8) sessionErrors.shift();
    await addDoc(collection(db, 'clientErrors'), {
      ...ctx(), kind, message: entry.message, stack: String(stack || '').slice(0, 1500), at: serverTimestamp(),
    });
  } catch { /* never throw from the logger */ }
}

export async function sendClientMessage(message: string): Promise<void> {
  const u = auth.currentUser;
  await addDoc(collection(db, 'clientMessages'), {
    ...ctx(),
    name: u?.displayName || '',
    message: String(message || '').slice(0, 2000),
    recentErrors: sessionErrors.slice(-5),
    status: 'open',
    at: serverTimestamp(),
  });
}

export function installErrorCapture() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => {
    logClientError(e.message || 'window.error', (e.error && e.error.stack) || `${e.filename}:${e.lineno}:${e.colno}`);
  });
  window.addEventListener('unhandledrejection', (e: any) => {
    const r = e?.reason;
    logClientError((r && r.message) || String(r) || 'unhandledrejection', r && r.stack, 'promise');
  });
}
